'use client';

import { Dialog } from '@headlessui/react';
import H1 from '../H1/H1';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

import styles from './Modal.module.sass';

interface ModalProps {
    title: string;
    isOpen?: boolean;
    isWide?: boolean;
    isFullScreen?: boolean;
    onClose: () => void;
    headerClassName?: string;
    contentClassName?: string;
    footer?: React.ReactNode;
    children: React.ReactNode;
}

const Modal = ({
    title,
    isOpen = true,
    isWide = false,
    isFullScreen = false,
    onClose,
    headerClassName = '',
    contentClassName = '',
    footer,
    children,
}: ModalProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const [hasScrolled, setHasScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setHasScrolled((scrollRef.current?.scrollTop ?? 0) > 0);
        };

        if (scrollRef.current)
            scrollRef.current.addEventListener('scroll', handleScroll);

        return () => {
            if (scrollRef.current)
                scrollRef.current.removeEventListener('scroll', handleScroll);
        };
    }, [scrollRef.current]);

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className={`${styles['modal']}`}
        >
            <motion.div
                className="fixed inset-0 bg-black/30"
                aria-hidden="true"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
            />

            <motion.div
                className={`${styles['modal__content']}`}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{
                    scale: 1,
                    opacity: 1,
                    transition: { ease: 'backOut' },
                }}
            >
                <Dialog.Panel
                    className={`${styles['modal__panel']} ${
                        isWide && styles['modal__panel--wide']
                    } ${isFullScreen && styles['modal__panel--fullscreen']}`}
                >
                    <H1
                        className={`z-10 shrink-0 rounded-t-2xl bg-primary p-4 text-center text-white ${
                            hasScrolled ? 'shadow-md' : ''
                        } transition-all duration-1000 ${headerClassName}`}
                    >
                        {title}
                    </H1>
                    <div
                        ref={scrollRef}
                        className={`${styles['modal__panel__scroll']} ${
                            !!footer && styles['modal__panel__scroll--footer']
                        } ${contentClassName}`}
                    >
                        {children}
                    </div>
                    {footer && (
                        <div>
                            <hr />
                            {footer}
                        </div>
                    )}
                </Dialog.Panel>
            </motion.div>
        </Dialog>
    );
};

export default Modal;
